import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NetworkMetrics } from '../../domain/metric-details.model';

@Component({
  selector: 'app-metric-details',
  templateUrl: './metric-details.component.html',
  styleUrls: ['./metric-details.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'app-metric-details' },
})
export class MetricDetailsComponent implements OnInit, OnChanges {
  private deviceColumnsAndFiltersMap: Map<string, string[]> = new Map([
    ['columns', ['device_name', 'source', 'time_frame', 'data_usage']],
    [
      'filters',
      [
        'deviceNameFilter',
        'sourceFilter',
        'timeframeFilter',
        'dataUsageFilter',
      ],
    ],
    [
      'filters2',
      [
        'deviceNameFilter2',
        'sourceFilter2',
        'timeframeFilter2',
        'dataUsageFilter2',
      ],
    ],
  ]);

  private applicationColumnsAndFiltersMap: Map<string, string[]> = new Map([
    ['columns', ['application', 'source', 'time_frame', 'data_usage']],
    [
      'filters',
      [
        'applicationFilter',
        'sourceFilter',
        'timeframeFilter',
        'dataUsageFilter',
      ],
    ],
    [
      'filters2',
      [
        'applicationFilter2',
        'sourceFilter2',
        'timeframeFilter2',
        'dataUsageFilter2',
      ],
    ],
  ]);

  private connectionColumnsAndFiltersMap: Map<string, string[]> = new Map([
    [
      'columns',
      [
        'link_name',
        'source',
        'time_frame',
        'data_utilization',
        'data_usage',
        'packet_loss',
        'latency',
        'jitter',
      ],
    ],
    [
      'filters',
      [
        'linkNameFilter',
        'sourceFilter',
        'timeframeFilter',
        'dataUtilizationFilter',
        'dataUsageFilter',
        'packetLossFilter',
        'latencyFilter',
        'jitterFilter',
      ],
    ],
    [
      'filters2',
      [
        'linkNameFilter2',
        'sourceFilter2',
        'timeframeFilter2',
        'dataUtilizationFilter2',
        'dataUsageFilter2',
        'packetLossFilter2',
        'latencyFilter2',
        'jitterFilter2',
      ],
    ],
  ]);

  private displayColumnsAndFiltersByDimension: Map<
    string,
    Map<string, string[]>
  > = new Map([
    ['device', this.deviceColumnsAndFiltersMap],
    ['application', this.applicationColumnsAndFiltersMap],
    ['connection', this.connectionColumnsAndFiltersMap],
  ]);

  displayedColumns: string[] = [];

  selectedDate: Date | null = null;

  showFilters = false; // Initial state, filters are hidden

  filters = [];

  filters2 = [];

  pageSizeOptions: number[] = [10, 25, 50, 75, 100];

  defaultPageSizeOption: number = 10;

  // Data source for the table
  @Input() dataSource: MatTableDataSource<NetworkMetrics>;

  @Input() loading: boolean = false; // Loading state

  @Input() isNoContent = false; // Display flag for content availability

  @Input() tableDimension: string = 'device'; // parameter for displaying different columns in the table;

  @ViewChild(MatSort, { static: true }) sort: MatSort; // Sorting reference

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator; // Pagination reference

  // Object to hold filter values for each column
  filterValues: { [key: string]: string } = {
    device_name: '',
    time_frame: '',
    latency: '',
    jitter: '',
    packet_loss: '',
    data_usage: '',
    data_utilization: '',
    application: '',
    link_name: '',
    source: '',
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
  };

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Boolean(changes.dataSource) && Boolean(this.dataSource)) {
      this.init();
    }
  }

  init(): void {
    if (this.tableDimension) {
      this.displayedColumns = this.displayColumnsAndFiltersByDimension
        .get(this.tableDimension)
        .get('columns');
      this.filters = this.displayColumnsAndFiltersByDimension
        .get(this.tableDimension)
        .get('filters');
      this.filters2 = this.displayColumnsAndFiltersByDimension
        .get(this.tableDimension)
        .get('filters2');
    }
    if (typeof this.dataSource === 'object') {
      this.dataSource.sort = this.sort;
      this.sort.active = 'data_usage';
      this.sort.direction = 'desc';
      this.sort.sortChange.emit({
        active: this.sort.active,
        direction: this.sort.direction,
      });
      this.dataSource.sortingDataAccessor = (item, property) => {
        if (property === 'time_frame') {
          return new Date(item.time_frame).getTime();
        }
        if (['latency', 'jitter'].includes(property)) {
          return Number(item[property]);
        }
        if (['packet_loss'].includes(property)) {
          return Number(item.packet_loss);
        }
        if (['data_usage'].includes(property)) {
          return Number(item.data_usage);
        }
        if (['data_utilization'].includes(property)) {
          return Number(item.data_utilization);
        }
        return item[property];
      };
      this.dataSource.paginator = this.paginator;

      // Custom filter predicate to filter by multiple columns
      this.dataSource.filterPredicate = this.createFilter();
      this.clearFilter();
    }
  }

  // Method to update the filter values and apply the filter
  applyFilter(event: Event, column: string): void {
    const filterValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();
    this.filterValues[column] = filterValue;
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  applyPicklistFilter(filterValue: string | null, column: string): void {
    this.filterValues[column] = filterValue
      ? filterValue.trim().toLowerCase()
      : '';
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  // Custom filter function to filter data based on multiple columns
  createFilter(): (data: NetworkMetrics, filter: string) => boolean {
    return (data: NetworkMetrics, filter: string): boolean => {
      const searchTerms = JSON.parse(filter) as { [key: string]: string };
      const timestamp = new Date(data.time_frame);

      // Handle date range filtering separately
      const hasDateFilter = searchTerms.startDate || searchTerms.endDate;
      if (hasDateFilter) {
        let isWithinDateRange = true;

        // Check start date/time constraint
        if (searchTerms.startDate) {
          const startDateTime = new Date(searchTerms.startDate);
          const [startHours, startMinutes] = this.convertTo24Hour(
            searchTerms.startTime || '12:00 AM',
          )
            .split(':')
            .map(Number);
          startDateTime.setHours(startHours, startMinutes, 0, 0);

          if (!Number.isNaN(startDateTime.getTime())) {
            isWithinDateRange = isWithinDateRange && timestamp >= startDateTime;
          }
        }

        // Check end date/time constraint
        if (searchTerms.endDate) {
          const endDateTime = new Date(searchTerms.endDate);
          const [startHours, startMinutes] = this.convertTo24Hour(
            searchTerms.endTime || '11:59 PM',
          )
            .split(':')
            .map(Number);
          endDateTime.setHours(startHours, startMinutes, 0, 0);

          if (!Number.isNaN(endDateTime.getTime())) {
            isWithinDateRange = isWithinDateRange && timestamp <= endDateTime;
          }
        }

        if (!isWithinDateRange) {
          return false;
        }
      }

      return Object.keys(searchTerms).every((key) => {
        const filterValue = searchTerms[key];

        if (!filterValue) return true; // Skip if no filter value

        // Skip date/time keys as they're handled above
        if (['startDate', 'startTime', 'endDate', 'endTime'].includes(key)) {
          return true;
        }

        // Handle numeric values for comparison
        if (
          [
            'latency',
            'jitter',
            'packet_loss',
            'data_usage',
            'data_utilization',
          ].includes(key)
        ) {
          return (
            searchTerms[key] === '' ||
            data[key as keyof NetworkMetrics]
              .toString()
              .indexOf(searchTerms[key]) !== -1
          );
        }
        return (
          searchTerms[key] === '' ||
          searchTerms[key] === null ||
          data[key as keyof NetworkMetrics]
            .toString()
            .toLowerCase()
            .indexOf(searchTerms[key]) !== -1
        );
      });
    };
  }

  clearFilter(): void {
    this.filterValues = {
      device_name: '',
      source: '',
      latency: '',
      jitter: '',
      packet_loss: '',
      dataUsage: '',
      application: '',
      data_utilization: '',
      link_name: '',
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    };
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  // Apply the date filter based on start and end dates
  applyDateFilter(): void {
    if (this.filterValues.startDate && !this.filterValues.startTime) {
      this.filterValues.startTime = this.getCurrentTimeIn12HourFormat();
    }
    if (this.filterValues.endDate && !this.filterValues.endTime) {
      this.filterValues.endTime = this.getCurrentTimeIn12HourFormat();
    }
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  // This method retrieves the current local time, formats it into the 12-hour clock format
  private getCurrentTimeIn12HourFormat(): string {
    const now = new Date();

    // Get hours and minutes
    let hours = now.getHours();
    const minutes = now.getMinutes();

    // Determine AM/PM and adjust hours
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Convert to 12-hour format and handle midnight (0 -> 12)

    // Format minutes to always have two digits
    const formattedMinutes = minutes.toString().padStart(2, '0');

    // Combine into 12-hour format string
    return `${hours}:${formattedMinutes} ${ampm}`;
  }

  // Convert 12-hour format time to 24-hour format for UTC parsing
  private convertTo24Hour(time12h: string): string {
    const [time, modifier] = time12h.split(' ');
    // eslint-disable-next-line prefer-const
    let [hours, minutes] = time.split(':');

    if (hours === '12') {
      hours = '00';
    }

    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  }
}
